/**
 * CreateTaskDialog — "New task" on the Tasks page (task management R1). A team task: live on save, routed to a role
 * queue (spec C15), or claimed at once when "Me" is chosen.
 *
 * Fields: the patient (required: the store keeps every task on a chart), what needs doing (≤ 80 characters, the only text
 * a task carries — C1/C12, so there is no free-text note), the kind, who it is for (Me or a role queue), when it is due
 * (amount from today; nobody types a date, §12), and urgent or routine.
 *
 * Pieces: Signal DS Modal, Select, TextField, Checkbox, Button, Banner; the app's patient search (`usePatientSearch`).
 */
import { useMemo, useState } from 'react';
import { Banner, Button, Checkbox, Modal, Select, TextField, TEXT_ROLE_CLASSES } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { usePatientSearch, type PatientSearchResult } from '@/services/scheduling/use-patient-search';
import {
  buildCreateBody,
  describeTaskError,
  DUE_CHOICES,
  MAX_DESCRIPTION,
  ROLE_LABEL,
  TASK_KINDS,
  TASK_ROLES,
  type TaskRole,
} from '@/tasks/lib/clinical-task-wire';
import { useCreateTask } from '@/tasks/services/use-clinical-tasks';

export const ME = 'me';

export interface CreateTaskDialogProps {
  open: boolean;
  onClose: () => void;
  /** The caller's own role queue: "Me" creates the task there and claims it. */
  myRole: TaskRole;
  onCreated?: (summary: string) => void;
}

const patientLabel = (p: PatientSearchResult) => [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ') + (p.mrn ? ` · ${p.mrn}` : '');

export function CreateTaskDialog({ open, onClose, myRole, onCreated }: CreateTaskDialogProps) {
  const organizationId = useAuthStore((s) => s.user?.organizationId ?? '');
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 300);
  const search = usePatientSearch({ organizationId, query: debounced, pageSize: 8 });
  const [patient, setPatient] = useState<PatientSearchResult | null>(null);
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('admin-other');
  const [assignee, setAssignee] = useState<string>(ME);
  const [due, setDue] = useState('1d');
  const [urgent, setUrgent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const create = useCreateTask();

  const results = useMemo(() => (debounced.trim().length >= 2 && !patient ? (search.data ?? []).slice(0, 8) : []), [debounced, patient, search.data]);
  const assigneeOptions = [{ label: 'Me', value: ME }, ...TASK_ROLES.map((r) => ({ label: ROLE_LABEL[r], value: r as string }))];

  const submit = () => {
    setFormError(null);
    if (!patient) return setFormError('Choose the patient.');
    const performerType = assignee === ME ? myRole : (assignee as TaskRole);
    const built = buildCreateBody({ kind, description, performerType, due, priority: urgent ? 'urgent' : 'routine' });
    if ('error' in built) return setFormError(built.error);
    create.mutate(
      { patientId: patient.id, body: built.body, claim: assignee === ME },
      {
        onSuccess: ({ claimFailed }) => {
          onCreated?.(
            claimFailed
              ? `Task created in the ${ROLE_LABEL[performerType]} queue, but it could not be claimed for you: claim it from the Team queue.`
              : assignee === ME
                ? 'Task added to My tasks.'
                : `Task added to the ${ROLE_LABEL[performerType]} queue.`,
          );
          onClose();
        },
      },
    );
  };

  const footer = (
    <>
      <Button type="button" variant="secondary" onClick={onClose} disabled={create.isPending}>
        Cancel
      </Button>
      <Button type="button" onClick={submit} disabled={create.isPending}>
        {create.isPending ? 'Saving…' : 'Create task'}
      </Button>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title="New task" size="md" footer={footer}>
      <div className="flex flex-col gap-4" data-slot="create-task-form">
        <div className="flex flex-col gap-2">
          {patient ? (
            <div className="flex items-center gap-2" data-slot="create-task-patient">
              <span className={TEXT_ROLE_CLASSES['body-emphasis']}>{patientLabel(patient)}</span>
              <Button type="button" size="sm" variant="tertiary" onClick={() => setPatient(null)}>
                Change
              </Button>
            </div>
          ) : (
            <TextField label="Patient" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or MRN" />
          )}
          {results.length > 0 ? (
            <ul className="flex flex-col gap-1" aria-label="Matching patients">
              {results.map((p) => (
                <li key={p.id}>
                  <Button type="button" size="sm" variant="tertiary" onClick={() => setPatient(p)}>
                    {patientLabel(p)}
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <TextField
          label="What needs doing"
          value={description}
          maxLength={MAX_DESCRIPTION}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Call the patient with the ferritin result"
          helperText={`${description.trim().length} / ${MAX_DESCRIPTION}`}
        />
        <Select label="Kind" options={TASK_KINDS.map((k) => ({ label: k.label, value: k.code }))} value={kind} onChange={setKind} />
        <Select label="Assign to" options={assigneeOptions} value={assignee} onChange={setAssignee} />
        <Select label="Due" options={DUE_CHOICES.map((d) => ({ label: d.label, value: d.value }))} value={due} onChange={setDue} />
        <Checkbox label="Urgent" checked={urgent} onCheckedChange={(c) => setUrgent(c === true)} />
        {formError || create.error ? (
          <Banner variant="error" role="alert">
            {formError ?? describeTaskError(create.error)}
          </Banner>
        ) : null}
      </div>
    </Modal>
  );
}
